import { Location } from '@angular/common';
import { Injectable, Injector } from '@angular/core';
import { AppService } from '@app/app.service';
import { AuthUser } from '@app/core/models/auth-user';
import { OidcSettings } from '@app/portal/models/app-info';
import { RouterService } from '@app/routing/router.service';
import { LocalStorageService } from '@app/shared/services/local-storage.service';
import { loggerFor } from '@app/utils/logger';
import Keycloak, { KeycloakError } from 'keycloak-js';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { AuthUtilsService } from './auth-utils.service';
import { AuthenticationService } from './authentication.service';

const OIDC_AFTER_LOGIN_REDIRECT_URI = 'OIDC_AFTER_LOGIN_REDIRECT_URI';
const OIDC_ERROR = 'OIDC_ERROR';

@Injectable({
  providedIn: 'root',
})
export class OIDCService {
  private logger = loggerFor('OIDCService');

  private keycloak: Keycloak | null = null;

  private _userAuthenticated$ = new ReplaySubject<boolean>(1);

  /** use case: should the login page/form be visible on not. */
  get userAuthenticated$(): Observable<boolean> {
    return this._userAuthenticated$.asObservable();
  }

  constructor(
    private authService: AuthenticationService,
    private authUtilsService: AuthUtilsService,
    private location: Location,
    private localStorage: LocalStorageService,
    private routerService: RouterService,
    private injector: Injector
  ) {
    this.authService.isAuthenticated$.subscribe((isAuthenticated: boolean) => {
      this._userAuthenticated$.next(isAuthenticated);
    });
  }

  init(oidcSettings: OidcSettings): void {
    if (oidcSettings.oidcEnabled === true) {
      this.keycloak = new Keycloak({
        url: oidcSettings.url,
        realm: oidcSettings.realm,
        clientId: oidcSettings.clientId,
      });

      /**
       * Called when the adapter is initialized.
       */
      this.keycloak.onReady = (authenticated?: boolean) => {
        this.logger.debug('onReady - authenticated = ', authenticated);
      };

      /**
       * Called when a user is successfully authenticated.
       */
      this.keycloak.onAuthSuccess = () => {
        this.logger.debug('onAuthSuccess');
      };

      /**
       * Called if there was an error during authentication.
       */
      this.keycloak.onAuthError = (errorData: KeycloakError) => {
        this.logger.debug('onAuthError', errorData);
      };

      /**
       * Called when the token is refreshed.
       */
      this.keycloak.onAuthRefreshSuccess = () => {
        this.logger.debug('onAuthRefreshSuccess');
      };

      /**
       * Called if there was an error while trying to refresh the token.
       */
      this.keycloak.onAuthRefreshError = () => {
        this.logger.debug('onAuthRefreshError');
      };

      /**
       * Called if the user is logged out (will only be called if the session
       * status iframe is enabled, or in Cordova mode).
       */
      this.keycloak.onAuthLogout = () => {
        // when auto-refreshing a token fails, due to removed keycloak session then this callback is call.
        // we logout at backend in that case.
        // note: auto-refreshing occurs every 5 minutes per default
        this.logger.debug('onAuthLogout');
        this.authService.isAuthenticated$.pipe(take(1)).subscribe((authenticated) => {
          if (authenticated === true) {
            this.authService
              .logout()
              .pipe(
                tap(() => {
                  // this.authUtilsService.redirectToLogin(window.location.href);
                  this.authUtilsService.redirectToLogin(this.location.path(true));
                })
              )
              .subscribe();
          }
        });
      };

      /**
       * Called when the access token is expired. If a refresh token is available
       * the token can be refreshed with Keycloak#updateToken, or in cases where
       * it's not (ie. with implicit flow) you can redirect to login screen to
       * obtain a new access token.
       */
      this.keycloak.onTokenExpired = () => {
        this.logger.debug('OnAccessTokenExpired');
        if (this.keycloak != null) {
          // we call this only to check the session status. if the session is expired then `onAuthLogout` is called.
          this.keycloak.updateToken().catch((reason: any) => {
            // ignore the reason. if something went wrong the logout will be performed anyway.
          });
        }
      };

      this.authService.onLogout$.subscribe(() => {
        // this.logout(window.location.origin + '/login').subscribe();
        this.logout(window.location.origin + this.location.prepareExternalUrl('/login')).subscribe();
      });
    } else {
      this.keycloak = null;
    }
  }

  initAndCheckSSO(oidcSettings: OidcSettings, authUser?: AuthUser): Observable<AuthUser | null> {
    this.init(oidcSettings);

    return this.checkSSO(authUser);
  }

  /**
   * Redirects to keycloak with username hint.
   * Note: The operation saves the current URL in the local storage and restores it after successful login.
   * @param username
   * @param oidcSettings
   * @returns
   */
  login(username: string, oidcSettings: OidcSettings): Observable<void> {
    let result$: Observable<void> = null;

    const url: string = window.history.state?.triedUrl ?? window.location.href;
    this.localStorage.setItem(OIDC_AFTER_LOGIN_REDIRECT_URI, url);

    // it might happen while the portal is loaded that on backend the keycloak was deactivated and then reactivated
    // in which case we must first initialize the keycloak
    if (this.keycloak == null && oidcSettings != null && oidcSettings.oidcEnabled === true) {
      const appService = this.injector.get(AppService);
      this.initAndCheckSSO(oidcSettings);
      return from(
        this.keycloak.login({
          loginHint: username,
        })
      );
    } else {
      result$ = from(
        this.keycloak.login({
          loginHint: username,
        })
      );
    }

    return result$;
  }

  /**
   * Logout current user from keycloak.
   * @param redirectUri is where the browser is redirected after logout.
   * @param delay optional millis to delay keycloak logout action.
   * @returns `true` if the OIDC is enabled and the user is logged-in, `false` otherwise.
   */
  logout(redirectUri: string, delay?: number): Observable<boolean> {
    if (this.keycloak != null && this.keycloak.authenticated === true) {
      if (delay > 0) {
        return of(true).pipe(
          tap(() => {
            setTimeout(
              () =>
                this.keycloak.logout({
                  redirectUri,
                }),
              delay
            );
          })
        );
      } else {
        return from(
          this.keycloak.logout({
            redirectUri,
          })
        ).pipe(map(() => true));
      }
    }

    return of(false);
  }

  private checkSSO(authUser: AuthUser | null): Observable<AuthUser | null> {
    if (this.keycloak == null) {
      this._userAuthenticated$.next(authUser != null);
      return of(authUser);
    } else {
      return from(
        this.keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: `${window.location.origin}${this.location.prepareExternalUrl('/assets/silent-check-sso.html')}`,
          checkLoginIframe: false,
        })
      ).pipe(
        mergeMap((authenticated) => {
          const afterLoginRedirectURI =
            this.localStorage.getItem<string>(OIDC_AFTER_LOGIN_REDIRECT_URI) ?? '';
          this.afterAppInitialization(authenticated);

          if (authenticated) {
            this.logger.debug('User is authenticated');

            // if `authUser` is `null` check if we are in login process, otherwise logout the user on keycloak
            if (authUser == null) {
              if (afterLoginRedirectURI.length > 0) {
                return this.authService.oidcAuthentication(this.keycloak.token).pipe(
                  tap((au: AuthUser) => {
                    this.authService.setAuthUser(au);
                  }),
                  catchError((err) => {
                    this.logger.error(
                      'OIDC authentication did not work. Check backend configuration. Resetting login process.',
                      err
                    );

                    this.localStorage.setItem(OIDC_ERROR, 'oidc.error.token.could.not.be.verified');
                    return from(this.keycloak.logout());
                  })
                );
              } else {
                return from(this.keycloak.logout());
              }
            }
          } else {
            this.logger.debug('User is not authenticated');
            if (authUser != null) {
              return this.authService.logout().pipe(
                tap(() => {
                  this.authUtilsService.redirectToLogin();
                }),
                map((_) => null)
              );
            }
          }

          return of(authUser);
        }),
        catchError((err: any) => {
          this.logger.error('Could not initialize keycloak-js', err);
          return of(null);
        })
      );
    }
  }

  /**
   * If there was a problem on backend trying to use the token or to access the keycloak.
   * It will be used in login page implementation to show an error that might happen between redirects.
   * The call to the function returns the value from the local storage and removes it from the there, meaning if called again it will return `null`.
   */
  oidcAuthenticationError(): string | null {
    const error: string | null = this.localStorage.getItem(OIDC_ERROR);
    if (error != null) {
      this.localStorage.removeItem(OIDC_ERROR);
    }
    return error;
  }

  userAccountURL(): string | null {
    return this.keycloak?.createAccountUrl();
  }

  private afterAppInitialization(authenticated: boolean): void {
    this.injector
      .get(AppService)
      .initialized$.pipe(
        take(1),
        tap(() => {
          this._userAuthenticated$.next(authenticated);
          const afterLoginRedirectURI =
            this.localStorage.getItem<string>(OIDC_AFTER_LOGIN_REDIRECT_URI) ?? '';
          if (authenticated === true) {
            this.localStorage.removeItem(OIDC_AFTER_LOGIN_REDIRECT_URI);
          }
          if (authenticated === true && afterLoginRedirectURI.length > 0) {
            if (afterLoginRedirectURI.includes('/login') === true) {
              return from(this.routerService.navigateToInitialRoute());
            } else {
              return from(this.routerService.navigateByUrl(afterLoginRedirectURI));
            }
          }
        })
      )
      .subscribe(() => {});
  }
}
